<template>
  <v-dialog v-model="dialog" max-width="520" persistent :fullscreen="$vuetify.breakpoint.smAndDown">
    <v-card>
      <v-card-title class="reset-title">Réinitialiser le stock</v-card-title>
      <v-card-text>
        <div class="field-label">Emplacement *</div>
        <v-select
          v-model="dialogStoreId" :items="stores" item-text="name" item-value="id"
          outlined dense hide-details class="mb-3" :loading="loadingStores"
          placeholder="Sélectionner l'emplacement"
        />

        <div class="field-label">Portée *</div>
        <v-radio-group v-model="scope" row dense class="mt-1 mb-0">
          <v-radio label="Tous les produits" value="all" />
          <v-radio label="Une catégorie" value="category" />
          <v-radio label="Un produit" value="product" />
        </v-radio-group>

        <v-select
          v-if="scope === 'category'"
          v-model="categoryId" :items="categories" item-text="name" item-value="id"
          outlined dense hide-details class="mb-3" placeholder="Choisir la catégorie"
        />
        <v-autocomplete
          v-if="scope === 'product'"
          v-model="productId" :items="products" item-text="name" item-value="id"
          outlined dense hide-details class="mb-3" placeholder="Rechercher le produit"
        />

        <v-alert dense text color="primary" class="preview-box">
          <strong>Aperçu</strong> — {{ preview.count }} produit(s) concerné(s) —
          {{ preview.units }} unité(s) seront annulées
          <span v-if="preview.negatives"> (dont {{ preview.negatives }} stock(s) négatif(s) remontés à 0)</span>
          (estimation — le serveur fait foi)
        </v-alert>

        <div class="field-label">Mémo (optionnel)</div>
        <v-text-field
          v-model="memo" outlined dense counter maxlength="200"
          class="mb-3" placeholder="Ex. : Inventaire physique juillet"
        />

        <v-alert dense type="error" text class="mb-3">
          Action irréversible. Le stock des produits sélectionnés sera remis à 0 via un lot d'ajustement.
        </v-alert>

        <div class="field-label">Taper <span class="reset-word">RESET</span> pour confirmer *</div>
        <v-text-field v-model="confirmText" outlined dense hide-details style="max-width:180px" />
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text :disabled="busy" @click="close">Annuler</v-btn>
        <v-btn color="error" :loading="busy" :disabled="!canSubmit" @click="submit">Réinitialiser</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'StockResetDialog',
  props: {
    products: { type: Array, default: () => [] },
    categories: { type: Array, default: () => [] },
    // Magasin sélectionné sur la page (/new-batche) : préselectionné à l'ouverture,
    // mais reste modifiable ici via son propre select (dialogStoreId).
    storeId: { type: [Number, String], default: null },
  },
  data: () => ({
    dialog: false,
    stores: [],
    loadingStores: false,
    dialogStoreId: null,
    scope: 'all',
    categoryId: null,
    productId: null,
    memo: '',
    confirmText: '',
    busy: false,
  }),
  computed: {
    scopedProducts() {
      if (this.scope === 'category' && this.categoryId) {
        return this.products.filter(p => p.category_id === this.categoryId)
      }
      if (this.scope === 'product' && this.productId) {
        return this.products.filter(p => p.id === this.productId)
      }
      if (this.scope === 'all') return this.products
      return []
    },
    preview() {
      const nonZero = this.scopedProducts.filter(p => Number(p.stock) !== 0)
      const units = nonZero.reduce((s, p) => s + Math.abs(Number(p.stock) || 0), 0)
      return {
        count: nonZero.length,
        units: Math.round(units * 100) / 100,
        negatives: nonZero.filter(p => Number(p.stock) < 0).length,
      }
    },
    canSubmit() {
      if (this.busy || this.confirmText !== 'RESET' || !this.dialogStoreId) return false
      if (this.scope === 'category' && !this.categoryId) return false
      if (this.scope === 'product' && !this.productId) return false
      return true
    },
  },
  methods: {
    open() {
      this.scope = 'all'
      this.categoryId = null
      this.productId = null
      this.memo = ''
      this.confirmText = ''
      // Préselection avec le magasin de la page ; reste modifiable via le select ci-dessus.
      this.dialogStoreId = this.storeId || null
      this.dialog = true
      this.loadStores()
    },
    close() {
      this.dialog = false
    },
    async loadStores() {
      this.loadingStores = true
      try {
        const businessId = JSON.parse(localStorage.business).id
        const res = await this.$axios.get('/stores?orderasc=name&business_id=' + businessId)
        this.stores = res.data
        if (!this.dialogStoreId && this.stores.length) this.dialogStoreId = this.stores[0].id
      } catch (e) {
        this.stores = []
        this.$emit('error', 'Impossible de charger les magasins')
      } finally {
        this.loadingStores = false
      }
    },
    async submit() {
      this.busy = true
      const user = this.$store.getters.StateUser || {}
      try {
        const res = await this.$axios.post('/operations/reset-stock', {
          confirm: this.confirmText,
          scope: this.scope,
          category_id: this.scope === 'category' ? this.categoryId : undefined,
          product_id: this.scope === 'product' ? this.productId : undefined,
          store_id: this.dialogStoreId,
          memo: this.memo || undefined,
          solgc_user: user.id,
          solgc_schema: this.$store.getters.StateSchema,
        })
        const d = res.data || {}
        if (d.success) {
          this.dialog = false
          this.$emit('done', { batch_no: d.batch_no, products_reset: d.products_reset, message: d.message })
        } else {
          this.$emit('error', d.message || 'Échec de la réinitialisation')
        }
      } catch (e) {
        const respData = e.response && e.response.data
        this.$emit('error', (respData && respData.message) || 'Échec de la réinitialisation')
      } finally {
        this.busy = false
      }
    },
  },
}
</script>

<style scoped>
.reset-title { color: #194173; font-weight: 700; border-bottom: 2px solid #f68d3b; }
.field-label { color: #194173; font-weight: 700; font-size: 12px; text-transform: uppercase; letter-spacing: .5px; margin-bottom: 4px; }
.reset-word { color: #F44336; font-weight: 900; letter-spacing: 1px; }
.preview-box { font-size: 13px; }
</style>
